import { type Edition } from "../../api/editions.service";
import { directories } from "../../api/directories.service";
import { editions } from "../../api/editions.service";
import { researchCms } from "../../api/researchCms.service";
import type { HierarchyManagerConfig } from "../../components/HierarchyManager";
import type { ZodType } from "zod";
import { EditionFormSchema } from "../../schemas/forms";

export type EditionWithHierarchy = Edition & {
  parentId?: string;
  displayOrder?: number;
  children?: EditionWithHierarchy[];
  [key: string]: unknown;
};

export const editionConfig: HierarchyManagerConfig<EditionWithHierarchy> = {
  title: "Editions",
  singularName: "Edition",
  pluralName: "Editions",

  apiService: {
    getAll: async () => {
      const editionsList = await editions.getAll();
      return editionsList.map(edition => ({
        ...edition,
        parentId: undefined,
        displayOrder: 0,
        children: [],
      }));
    },
    getById: async (id: string) => {
      const edition = await editions.getById(id);
      return {
        ...edition,
        parentId: undefined,
        displayOrder: 0,
        children: [],
      };
    },
    create: async (data: Record<string, unknown>) => {
      const edition = await editions.create(data as Parameters<typeof editions.create>[0]);
      await researchCms.createEditionConfig({ editionId: edition.id }).catch(() => undefined);
      return {
        ...edition,
        parentId: undefined,
        displayOrder: 0,
        children: [],
      };
    },
    update: async (id: string, data: Record<string, unknown>) => {
      const edition = await editions.update(id, data as Parameters<typeof editions.update>[1]);
      return {
        ...edition,
        parentId: undefined,
        displayOrder: 0,
        children: [],
      };
    },
    delete: (id: string) => editions.delete(id),
  },

  formFields: [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      placeholder: "Enter edition name (e.g., Legal 500 UK 2024)",
      defaultValue: "",
    },
    {
      name: "code",
      label: "Code",
      type: "text",
      required: true,
      placeholder: "Enter edition code (e.g., uk-2024)",
      defaultValue: "",
    },
    {
      name: "directoryId",
      label: "Directory",
      type: "select",
      required: true,
      searchable: true,
      defaultValue: null,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      options: [
        { value: "draft", label: "Draft" },
        { value: "published", label: "Published" },
        { value: "archived", label: "Archived" },
      ],
      defaultValue: "draft",
    },
    {
      name: "publishedDate",
      label: "Published Date",
      type: "date",
      required: false,
      helperText: "Required when status is 'published'",
      defaultValue: null,
    },
  ],

  validationSchema: EditionFormSchema,

  enableDragAndDrop: false,
  enableSearch: true,
  enableBreadcrumbs: false,
  showCode: true,
  treeMode: "list",
};

export async function loadDirectoryOptions() {
  const directoriesList = await directories.getAll();
  return directoriesList
    .map(dir => ({
      value: dir.id,
      label: `${dir.name} (${dir.code})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
